import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getServicesData } from '../../Public/services-data';
import { getServicePageUI } from '../../Public/service-page-data';
import NavLink from '../../Utils/NavLink/navlink-component';

function ServicesSection({
    limit = null,
    title = null,
    isHeadline = false,
    showMore = false,
    showMoreText = null,
    sectionId = null,
    onShowMore = null,
}) {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadedImages, setLoadedImages] = useState({});

    const systemData = useSelector((s) => s.systemData || {});
    const { dir, language } = systemData;

    const uiContent = getServicePageUI(language).servicesListSection;

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            try {
                const data = await getServicesData();
                setServices(data || []);
            } catch (err) {
                console.error('Failed to fetch services:', err);
            } finally {
                setLoading(false);
            }
        };
        initialize();
    }, []);

    const displayServices = useMemo(() => {
        const base = services.filter(s => s.isActive !== false && s.status !== false);
        return limit ? base.slice(0, limit) : base;
    }, [services, limit]);

    const skeletonCount = limit || 6;

    const allImagesLoaded = useMemo(() => {
        if (!displayServices.length) return false;
        return Object.keys(loadedImages).length >= displayServices.length;
    }, [loadedImages, displayServices]);

    const markImageLoaded = (idx) => setLoadedImages(prev => ({ ...prev, [idx]: true }));

    if (!loading && displayServices.length === 0) return null;

    return (
        <section id={sectionId || undefined} dir={dir} className="w-full bg-white py-12 lg:py-20">
            <div className="max-w-[1400px] mx-auto px-4 lg:px-8">

                {/* Header */}
                {(isHeadline || title) && (
                    <div className="text-center mb-12">
                        <h2 className="text-5xl lg:text-7xl font-normal text-secondary-500">
                            {title || uiContent?.headline || 'Our Services'}
                        </h2>
                        {isHeadline && uiContent?.subHeadline && (
                            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                                {uiContent.subHeadline}
                            </p>
                        )}
                    </div>
                )}

                {/* Grid */}
                <div className="relative min-h-[400px]">

                    {/* Skeleton layer */}
                    <div className={`
                        absolute inset-0 z-20 bg-white
                        grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4
                        transition-opacity duration-700
                        ${loading || !allImagesLoaded ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                    `}>
                        {[...Array(skeletonCount)].map((_, idx) => (
                            <div key={`skel-${idx}`} className="bg-white p-4 flex flex-col animate-pulse border border-gray-100">
                                <div className="w-full aspect-[5/3] mb-4 bg-gray-200 rounded" />
                                <div className="h-7 w-3/4 bg-gray-200 rounded mb-3" />
                                <div className="h-4 w-1/3 bg-secondary-100 rounded" />
                            </div>
                        ))}
                    </div>

                    {/* Content layer */}
                    <div className={`
                        relative z-10
                        grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4
                        transition-opacity duration-700
                        ${!loading && allImagesLoaded ? 'opacity-100' : 'opacity-0'}
                    `}>
                        {displayServices.map((svc, idx) => {
                            const img    = svc.cardImage || svc.image;
                            const name   = svc.cardTitle || svc.name;
                            const href   = svc.slug ? `/services/${svc.slug}` : '/services';
                            const btnTxt = svc.cardButtonText || uiContent?.learnMore || 'Learn More';

                            return (
                                <NavLink
                                    key={svc.id || idx}
                                    href={href}
                                    showUnderline={false}
                                    className="!py-0 !opacity-100 !block group bg-white p-4 flex flex-col items-start text-left hover:shadow-lg transition-all duration-300 border border-transparent hover:border-gray-100 w-full"
                                >
                                    {/* Image */}
                                    <div className="w-full aspect-[5/3] mb-4 overflow-hidden bg-gray-100 relative">
                                        <img
                                            src={img}
                                            alt={name}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            onLoad={() => markImageLoaded(idx)}
                                            onError={() => markImageLoaded(idx)}
                                        />
                                    </div>

                                    {/* Title */}
                                    <h2 className="text-2xl font-bold text-black leading-snug mb-1">
                                        {name}
                                    </h2>

                                    {/* Learn More */}
                                    <div className="flex items-center gap-1 text-secondary-500 font-semibold mt-3 group-hover:underline decoration-2 underline-offset-4">
                                        <span>{btnTxt}</span>
                                        <svg
                                            className="w-4 h-4 transform transition-transform duration-300 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                                            fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                                        </svg>
                                    </div>
                                </NavLink>
                            );
                        })}
                    </div>
                </div>

                {/* Show More */}
                {showMore && (
                    <div className="flex justify-center mt-10">
                        <button
                            onClick={onShowMore}
                            className="border-2 border-secondary-500 text-secondary-500 hover:bg-secondary-500 hover:text-white font-bold text-sm px-10 py-3 transition-colors uppercase tracking-widest"
                        >
                            {showMoreText || 'View All Services'}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}

export default ServicesSection;
